/**
 * CLI Landing Page Management (superadmin-only)
 *
 * Lets a marketing agent create and A/B-test landing pages published at
 * runcofactor.com/lp/<slug> purely through this CLI — no deploy. Talks to the
 * superadmin CRUD routes in `apps/api/src/landing/crud-routes.ts`. See
 * `apps/landing/README.md` for the HTML authoring contract (CTA placeholders,
 * lead-capture forms, CSS custom properties) and
 * `docs/api/routes/landing-pages.md` for the end-to-end agent workflow.
 *
 * Superadmin gating happens in THREE places (see `docs/api/routes/landing-pages.md`
 * for the full rationale): the `landing` line only appears in `printHelp`'s
 * `isSuperadmin` block (help.ts), `runLanding` below re-checks
 * `resolveIsSuperadmin()` before doing anything (including the local-only
 * `url` sub-command), and the server itself gates every route through
 * `defineAdminRoute` → `withSuperadminAuth`. The CLI layers are UX only — the
 * server gate is the real boundary.
 */

import { readFile, writeFile, mkdir } from 'node:fs/promises';
import path from 'node:path';
import process from 'node:process';
import { getSubDir } from '@chatsdet/tmp';
import { envToProfile, getArgValue, hasFlag, resolveConfig } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import { resolveIsSuperadmin, showGlobalHelp } from './help.js';

const DEFAULT_PULL_DIR = getSubDir('landing');

/* ── API response shapes ─────────────────────────────────────────────── */

type LandingPageListItem = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  ogImage: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

type LandingPageVariant = {
  id: string;
  pageId: string;
  name: string;
  bodyHtml: string;
  weight: number;
  isActive: boolean;
  currentVersion: number;
  createdAt: string;
  updatedAt: string;
};

type LandingPage = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  ogImage: string | null;
  headHtml: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
};

type LandingPageDetail = { page: LandingPage; variants: LandingPageVariant[] };

type LandingVersion = {
  id: string;
  pageId: string;
  variantId: string | null;
  variantName: string | null;
  version: number;
  title?: string | null;
  bodyHtml?: string | null;
  weight: number | null;
  isActive: boolean | null;
  checksum: string;
  message: string | null;
  createdBy: string;
  createdVia: string;
  rolledBackFrom: number | null;
  createdAt: string;
};

type LandingStatsVariant = { variantName: string; views: number; repeatViews: number; leads: number };

type LandingApiResponse = {
  ok: boolean;
  error?: string;
  data?: {
    pages?: LandingPageListItem[];
    page?: LandingPage;
    variants?: LandingPageVariant[] | LandingStatsVariant[];
    slug?: string;
    title?: string;
    status?: string;
    versions?: LandingVersion[];
    variant?: LandingPageVariant & { currentVersion?: number };
    version?: number;
    rolledBackFrom?: number;
  } & Partial<LandingVersion>;
};

/* ── Pure helpers (unit-testable, no network) ────────────────────────── */

/**
 * Parse a `--since` flag into an ISO timestamp the API can filter on.
 * Accepts a bare duration (`7d`, `24h`, `30m`, or a bare number of days) and
 * converts it to `now - duration`. Anything else is passed through verbatim
 * so an already-ISO or already-`YYYY-MM-DD` value still reaches the server.
 */
export function parseSinceFlag(raw: string | undefined, now: number = Date.now()): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  const match = trimmed.match(/^(\d+)\s*(d|h|m)?$/i);
  if (!match) return trimmed;
  const amount = Number(match[1]);
  const unit = (match[2] ?? 'd').toLowerCase();
  const msPerUnit = unit === 'h' ? 3_600_000 : unit === 'm' ? 60_000 : 86_400_000;
  return new Date(now - amount * msPerUnit).toISOString();
}

/** Conversion rate as a display string. Guards divide-by-zero explicitly. */
export function formatCvr(leads: number, views: number): string {
  if (views <= 0) return '—';
  return `${((leads / views) * 100).toFixed(1)}%`;
}

export type StatsRow = { variant: string; weight: number | null; views: number; leads: number };

/** Render the default (non-JSON) `landing stats` output as a markdown-ish table. */
export function renderStatsTable(opts: {
  slug: string;
  sinceLabel: string;
  includeForced: boolean;
  rows: StatsRow[];
}): string {
  const { slug, sinceLabel, includeForced, rows } = opts;
  const lines: string[] = [
    `Stats for ${slug} (since ${sinceLabel}, forced views ${includeForced ? 'included' : 'excluded'})`,
    '',
  ];

  if (rows.length === 0) {
    lines.push('No stats recorded yet.');
    return lines.join('\n');
  }

  const variantWidth = Math.max(7, ...rows.map((r) => r.variant.length));
  lines.push(`| ${'Variant'.padEnd(variantWidth)} | Weight | Views | Leads | CVR   |`);
  lines.push(`| ${'-'.repeat(variantWidth)} | ------ | ----- | ----- | ----- |`);
  for (const row of rows) {
    const weight = row.weight === null ? '—' : String(row.weight);
    lines.push(
      `| ${row.variant.padEnd(variantWidth)} | ${weight.padEnd(6)} | ${String(row.views).padEnd(5)} | ${String(
        row.leads
      ).padEnd(5)} | ${formatCvr(row.leads, row.views).padEnd(5)} |`
    );
  }
  return lines.join('\n');
}

/**
 * Hostname the landing SSR app is reachable on for a given env label, mirrors
 * `infra/components/landing.ts`'s `landingHost`: production owns the
 * disposable `lp.runcofactor.com` subdomain (NOT the apex yet — see that
 * file's header comment), every other stage gets `lp.<stage>.runcofactor.com`.
 */
export function landingHostForEnv(envLabel: string): string {
  return envLabel === 'production' ? 'lp.runcofactor.com' : `lp.${envLabel}.runcofactor.com`;
}

/** Build the preview URL for a slug/variant pair. Pure, no network. */
export function buildLandingUrl(envLabel: string, slug: string, variant?: string): string {
  const host = landingHostForEnv(envLabel);
  const qs = variant ? `?v=${encodeURIComponent(variant)}` : '';
  return `https://${host}/lp/${slug}${qs}`;
}

/* ── IO helpers ───────────────────────────────────────────────────────── */

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) chunks.push(Buffer.from(chunk));
  return Buffer.concat(chunks).toString('utf-8');
}

/** Read HTML body content from a `--file <path>` flag; `-` reads stdin. */
async function readFileFlag(argv: string[], flag = '--file'): Promise<string> {
  const filePath = getArgValue(argv, flag);
  if (!filePath) {
    console.error(`Error: Missing ${flag} <path>.`);
    process.exit(1);
  }
  if (filePath === '-') return readStdin();
  try {
    return await readFile(filePath, 'utf8');
  } catch {
    console.error(`Error: Could not read file: ${filePath}`);
    process.exit(1);
  }
}

async function fetchDetail(
  apiUrl: string,
  token: string,
  slug: string
): Promise<LandingPageDetail> {
  const result = await apiRequest<LandingApiResponse>(
    apiUrl,
    token,
    'GET',
    `/superadmin/landing/pages/detail?slug=${encodeURIComponent(slug)}`
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'Landing page not found'}`);
    process.exit(1);
  }
  return result.data as unknown as LandingPageDetail;
}

function resolveVariant(detail: LandingPageDetail, variantArg: string | undefined): LandingPageVariant {
  if (variantArg) {
    const match = detail.variants.find((v) => v.name === variantArg);
    if (!match) {
      console.error(`Error: Variant "${variantArg}" not found on ${detail.page.slug}.`);
      console.error(`Available variants: ${detail.variants.map((v) => v.name).join(', ') || '(none)'}`);
      process.exit(1);
    }
    return match;
  }
  if (detail.variants.length === 0) {
    console.error(`Error: ${detail.page.slug} has no variants yet.`);
    process.exit(1);
  }
  if (detail.variants.length === 1) return detail.variants[0]!;
  const defaultVariant = detail.variants.find((v) => v.name === 'a');
  if (defaultVariant) return defaultVariant;
  console.error(`Error: ${detail.page.slug} has multiple variants — pass --variant.`);
  console.error(`Available variants: ${detail.variants.map((v) => v.name).join(', ')}`);
  process.exit(1);
}

/* ── Page commands ────────────────────────────────────────────────────── */

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const result = await apiRequest<LandingApiResponse>(apiUrl, token, 'GET', '/superadmin/landing/pages');
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  const pages = result.data?.pages ?? [];

  if (jsonOutput) {
    console.log(JSON.stringify(pages, null, 2));
    return;
  }

  if (pages.length === 0) {
    console.log('No landing pages found.');
    return;
  }

  const slugWidth = Math.max(4, ...pages.map((p) => p.slug.length));
  const titleWidth = Math.max(5, ...pages.map((p) => p.title.length));
  console.log(`| ${'Slug'.padEnd(slugWidth)} | ${'Title'.padEnd(titleWidth)} | Status    | Updated    |`);
  console.log(`| ${'-'.repeat(slugWidth)} | ${'-'.repeat(titleWidth)} | --------- | ---------- |`);
  for (const page of pages) {
    const date = new Date(page.updatedAt).toISOString().slice(0, 10);
    console.log(
      `| ${page.slug.padEnd(slugWidth)} | ${page.title.padEnd(titleWidth)} | ${page.status.padEnd(9)} | ${date} |`
    );
  }
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing get <slug> [--variant <name>]');
    process.exit(1);
  }
  const detail = await fetchDetail(apiUrl, token, slug);
  const variant = resolveVariant(detail, getArgValue(argv, '--variant'));
  console.log(variant.bodyHtml);
}

async function handlePull(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing pull <slug> [--variant <name>] [--output <path>]');
    process.exit(1);
  }
  const detail = await fetchDetail(apiUrl, token, slug);
  const variant = resolveVariant(detail, getArgValue(argv, '--variant'));

  const defaultName = `${slug}${variant.name === 'a' ? '' : `--${variant.name}`}.html`;
  const outputPath = getArgValue(argv, '--output') ?? path.join(DEFAULT_PULL_DIR, defaultName);

  await mkdir(path.dirname(outputPath), { recursive: true });
  await writeFile(outputPath, variant.bodyHtml, 'utf8');
  console.log(`Pulled ${slug} (variant ${variant.name}) → ${outputPath}`);
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing create <slug> --file <path> --title <text>');
    process.exit(1);
  }

  const title = getArgValue(argv, '--title');
  if (!title) {
    console.error('Error: Missing --title <text>.');
    process.exit(1);
  }

  const bodyHtml = await readFileFlag(argv);
  const variantName = getArgValue(argv, '--variant') ?? 'a';
  const description = getArgValue(argv, '--description');
  const ogImage = getArgValue(argv, '--og-image');
  const status = getArgValue(argv, '--status');

  const result = await apiRequest<LandingApiResponse>(apiUrl, token, 'POST', '/superadmin/landing/pages', {
    slug,
    title,
    description: description ?? undefined,
    ogImage: ogImage ?? undefined,
    status: status ?? undefined,
    variants: [{ name: variantName, bodyHtml }],
  });

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Created landing page: ${slug} (variant ${variantName})`);
}

async function handlePush(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing push <slug> --file <path> [--variant a] [--message <text>]');
    process.exit(1);
  }

  const bodyHtml = await readFileFlag(argv);
  const variantName = getArgValue(argv, '--variant') ?? 'a';
  const message = getArgValue(argv, '--message');

  const result = await apiRequest<LandingApiResponse>(
    apiUrl,
    token,
    'PUT',
    `/superadmin/landing/variants?slug=${encodeURIComponent(slug)}&variant=${encodeURIComponent(variantName)}`,
    { bodyHtml, message: message ?? undefined }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  const version = result.data?.variant?.currentVersion;
  console.log(`Pushed ${slug} (variant ${variantName})${version ? ` — v${version}` : ''}`);
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing set <slug> [--title] [--description] [--og-image] [--status ...]');
    process.exit(1);
  }

  const title = getArgValue(argv, '--title');
  const description = getArgValue(argv, '--description');
  const ogImage = getArgValue(argv, '--og-image');
  const status = getArgValue(argv, '--status');

  const body: Record<string, unknown> = {};
  if (title !== undefined) body.title = title;
  if (description !== undefined) body.description = description;
  if (ogImage !== undefined) body.ogImage = ogImage;
  if (status !== undefined) body.status = status;

  if (Object.keys(body).length === 0) {
    console.error('Error: No fields to update. Pass --title, --description, --og-image, or --status.');
    process.exit(1);
  }

  const result = await apiRequest<LandingApiResponse>(
    apiUrl,
    token,
    'PUT',
    `/superadmin/landing/pages?slug=${encodeURIComponent(slug)}`,
    body
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Updated landing page: ${slug}`);
}

async function handleRm(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing rm <slug>');
    process.exit(1);
  }

  const result = await apiRequest<LandingApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `/superadmin/landing/pages?slug=${encodeURIComponent(slug)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Deleted landing page: ${slug}`);
}

/* ── Variant sub-group ────────────────────────────────────────────────── */

async function handleVariant(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;

  switch (action) {
    case 'list': {
      const slug = rest[0];
      if (!slug || slug.startsWith('--')) {
        console.error('Usage: cofactor landing variant list <slug>');
        process.exit(1);
      }
      const detail = await fetchDetail(apiUrl, token, slug);
      if (detail.variants.length === 0) {
        console.log(`No variants for ${slug}.`);
        return;
      }
      console.log('| Name     | Weight | Active | Version |');
      console.log('| -------- | ------ | ------ | ------- |');
      for (const v of detail.variants) {
        console.log(
          `| ${v.name.padEnd(8)} | ${String(v.weight).padEnd(6)} | ${(v.isActive ? 'yes' : 'no').padEnd(
            6
          )} | ${String(v.currentVersion).padEnd(7)} |`
        );
      }
      return;
    }
    case 'add': {
      const slug = rest[0];
      const name = rest[1];
      if (!slug || !name || slug.startsWith('--') || name.startsWith('--')) {
        console.error('Usage: cofactor landing variant add <slug> <name> --file <path> [--weight <n>]');
        process.exit(1);
      }
      const bodyHtml = await readFileFlag(rest);
      const weightArg = getArgValue(rest, '--weight');
      const weight = weightArg !== undefined ? Number.parseInt(weightArg, 10) : undefined;
      if (weightArg !== undefined && Number.isNaN(weight)) {
        console.error('Error: --weight must be an integer.');
        process.exit(1);
      }
      const result = await apiRequest<LandingApiResponse>(
        apiUrl,
        token,
        'PUT',
        `/superadmin/landing/variants?slug=${encodeURIComponent(slug)}&variant=${encodeURIComponent(name)}`,
        { bodyHtml, weight }
      );
      if (!result.ok) {
        console.error(`Error: ${result.error}`);
        process.exit(1);
      }
      console.log(`Added variant ${name} to ${slug}`);
      return;
    }
    case 'set': {
      const slug = rest[0];
      const name = rest[1];
      if (!slug || !name || slug.startsWith('--') || name.startsWith('--')) {
        console.error(
          'Usage: cofactor landing variant set <slug> <name> [--weight <n>] [--active|--inactive] [--file <path>]'
        );
        process.exit(1);
      }
      const weightArg = getArgValue(rest, '--weight');
      const weight = weightArg !== undefined ? Number.parseInt(weightArg, 10) : undefined;
      if (weightArg !== undefined && Number.isNaN(weight)) {
        console.error('Error: --weight must be an integer.');
        process.exit(1);
      }
      const isActive = hasFlag(rest, '--active') ? true : hasFlag(rest, '--inactive') ? false : undefined;
      const bodyHtml = hasFlag(rest, '--file') ? await readFileFlag(rest) : undefined;

      if (weight === undefined && isActive === undefined && bodyHtml === undefined) {
        console.error('Error: No fields to update. Pass --weight, --active/--inactive, or --file.');
        process.exit(1);
      }

      const result = await apiRequest<LandingApiResponse>(
        apiUrl,
        token,
        'PUT',
        `/superadmin/landing/variants?slug=${encodeURIComponent(slug)}&variant=${encodeURIComponent(name)}`,
        { bodyHtml, weight, isActive }
      );
      if (!result.ok) {
        console.error(`Error: ${result.error}`);
        process.exit(1);
      }
      console.log(`Updated variant ${name} on ${slug}`);
      return;
    }
    case 'rm': {
      const slug = rest[0];
      const name = rest[1];
      if (!slug || !name || slug.startsWith('--') || name.startsWith('--')) {
        console.error('Usage: cofactor landing variant rm <slug> <name>');
        process.exit(1);
      }
      const result = await apiRequest<LandingApiResponse>(
        apiUrl,
        token,
        'DELETE',
        `/superadmin/landing/variants?slug=${encodeURIComponent(slug)}&variant=${encodeURIComponent(name)}`
      );
      if (!result.ok) {
        console.error(`Error: ${result.error}`);
        process.exit(1);
      }
      console.log(`Deleted variant ${name} from ${slug}`);
      return;
    }
    default:
      console.error(`Unknown variant action: ${action ?? '(none)'}`);
      console.error('Actions: list <slug>, add <slug> <name>, set <slug> <name>, rm <slug> <name>');
      process.exit(1);
  }
}

/* ── History / rollback ──────────────────────────────────────────────── */

async function handleHistory(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing history <slug> [--variant <name>]');
    process.exit(1);
  }
  const jsonOutput = hasFlag(argv, '--json');
  const variant = getArgValue(argv, '--variant');
  const qs = new URLSearchParams({ slug });
  if (variant) qs.set('variant', variant);

  const result = await apiRequest<LandingApiResponse>(apiUrl, token, 'GET', `/superadmin/landing/versions?${qs}`);
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  const versions = result.data?.versions ?? [];

  if (jsonOutput) {
    console.log(JSON.stringify(versions, null, 2));
    return;
  }

  if (versions.length === 0) {
    console.log(`No version history for ${slug}.`);
    return;
  }

  for (const v of versions) {
    const date = new Date(v.createdAt).toISOString().slice(0, 19).replace('T', ' ');
    const label = v.variantName ?? '(page)';
    const msg = v.message ? ` — ${v.message}` : '';
    const rollback = v.rolledBackFrom ? ` [rollback from v${v.rolledBackFrom}]` : '';
    console.log(`  ${label} v${v.version}  ${date}  ${v.createdBy}${msg}${rollback}`);
  }
}

async function handleRollback(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing rollback <slug> --variant <name> --version <n>');
    process.exit(1);
  }
  const variant = getArgValue(argv, '--variant');
  const versionArg = getArgValue(argv, '--version');
  if (!variant) {
    console.error('Error: Missing --variant <name>.');
    process.exit(1);
  }
  if (!versionArg) {
    console.error('Error: Missing --version <n>.');
    process.exit(1);
  }
  const version = Number.parseInt(versionArg, 10);
  if (Number.isNaN(version)) {
    console.error('Error: --version must be an integer.');
    process.exit(1);
  }

  const qs = new URLSearchParams({ slug, variant, version: String(version) });
  const result = await apiRequest<LandingApiResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/landing/rollback?${qs}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  console.log(`Rolled back ${slug} (variant ${variant}) to v${result.data?.rolledBackFrom} → new v${result.data?.version}`);
}

/* ── Stats ────────────────────────────────────────────────────────────── */

async function handleStats(argv: string[], apiUrl: string, token: string): Promise<void> {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing stats <slug> [--since 7d] [--include-forced] [--json]');
    process.exit(1);
  }
  const jsonOutput = hasFlag(argv, '--json');
  const includeForced = hasFlag(argv, '--include-forced');
  const sinceRaw = getArgValue(argv, '--since');
  const since = parseSinceFlag(sinceRaw);

  const qs = new URLSearchParams({ slug });
  if (since) qs.set('since', since);
  if (includeForced) qs.set('includeForced', 'true');

  const [detail, statsResult] = await Promise.all([
    fetchDetail(apiUrl, token, slug),
    apiRequest<LandingApiResponse>(apiUrl, token, 'GET', `/superadmin/landing/stats?${qs}`),
  ]);

  if (!statsResult.ok) {
    console.error(`Error: ${statsResult.error}`);
    process.exit(1);
  }
  const statsVariants = (statsResult.data?.variants ?? []) as LandingStatsVariant[];

  if (jsonOutput) {
    console.log(JSON.stringify(statsVariants, null, 2));
    return;
  }

  const weightByName = new Map(detail.variants.map((v) => [v.name, v.weight]));
  const rows: StatsRow[] = statsVariants
    .map((v) => ({
      variant: v.variantName,
      weight: weightByName.get(v.variantName) ?? null,
      views: v.views,
      leads: v.leads,
    }))
    .sort((a, b) => a.variant.localeCompare(b.variant));

  console.log(
    renderStatsTable({
      slug,
      sinceLabel: sinceRaw ?? 'all time',
      includeForced,
      rows,
    })
  );
}

/* ── url (local-only, no API call) ───────────────────────────────────── */

function handleUrl(argv: string[]): void {
  const slug = argv[0];
  if (!slug || slug.startsWith('--')) {
    console.error('Error: Missing slug.');
    console.error('Usage: cofactor landing url <slug> [--variant <name>]');
    process.exit(1);
  }
  const variant = getArgValue(argv, '--variant');
  const envArg = getArgValue(argv, '--env') ?? 'prod';
  const envLabel = envToProfile(envArg);
  console.log(buildLandingUrl(envLabel, slug, variant));
}

/* ── help + dispatch ──────────────────────────────────────────────────── */

function printLandingHelp(): void {
  console.log(
    [
      'Usage: cofactor landing <sub-command> [options]',
      '',
      'Create and A/B-test landing pages at runcofactor.com/lp/<slug> — no deploy.',
      'See apps/landing/README.md for the HTML authoring contract.',
      '',
      'Sub-commands:',
      '  list [--json]                                List all landing pages',
      '  get <slug> [--variant <name>]                 Print a variant body to stdout',
      '  pull <slug> [--variant <name>] [--output <p>] Download a variant to a local file',
      '  create <slug> --file <p> --title <t>          Create a page + its first variant',
      '           [--description <t>] [--og-image <u>] [--variant a] [--status draft|published]',
      '  push <slug> --file <p> [--variant a] [--message <t>]  Publish a new variant body',
      '  set <slug> [--title] [--description] [--og-image] [--status draft|published|archived]',
      '  rm <slug>                                     Soft-delete a page',
      '  variant list <slug>                           List variants + weights/active state',
      '  variant add <slug> <name> --file <p> [--weight <n>]  Add a new variant',
      '  variant set <slug> <name> [--weight <n>] [--active|--inactive] [--file <p>]',
      '  variant rm <slug> <name>                      Delete a variant',
      '  history <slug> [--variant <name>] [--json]    Show version history',
      '  rollback <slug> --variant <name> --version <n>  Roll a variant back to a prior version',
      '  stats <slug> [--since 7d] [--include-forced] [--json]  Views/leads/CVR per variant',
      '  url <slug> [--variant <name>]                 Print the preview URL (no API call)',
      '',
      'Options:',
      '  --file <path>          Path to an HTML file, or "-" to read from stdin',
      '  --json                 Output as JSON (list, history, stats)',
      '  --env <env>            Target environment (prod, dev, local)',
      '  --api-url <url>        API URL override',
      '  --token <key>          API token override',
    ].join('\n')
  );
}

export async function runLanding(argv: string[]): Promise<void> {
  // Superadmin-only, gated client-side (no server 403 to lean on for the
  // local-only `url` sub-command, and every other sub-command should read as
  // "this command doesn't exist" rather than leaking a 403 to non-superadmins).
  if (!(await resolveIsSuperadmin())) {
    console.log('Unknown command "landing".');
    await showGlobalHelp();
    process.exit(1);
  }

  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printLandingHelp();
    return;
  }

  if (subCommand === 'url') {
    handleUrl(rest);
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'pull':
      await handlePull(rest, apiUrl, token);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      break;
    case 'push':
      await handlePush(rest, apiUrl, token);
      break;
    case 'set':
      await handleSet(rest, apiUrl, token);
      break;
    case 'rm':
      await handleRm(rest, apiUrl, token);
      break;
    case 'variant':
      await handleVariant(rest, apiUrl, token);
      break;
    case 'history':
      await handleHistory(rest, apiUrl, token);
      break;
    case 'rollback':
      await handleRollback(rest, apiUrl, token);
      break;
    case 'stats':
      await handleStats(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printLandingHelp();
      process.exit(1);
  }
}
